import React, { useState, useEffect } from 'react';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SEOHead from '../components/SEOHead';
import axios from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';

const ContactUs: React.FC = () => {
  useDocumentTitle('Cognidaily | Contact Us');
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({ ...prev, name: user.username || '', email: user.email || '' }));
    }
  }, [isAuthenticated, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setSuccess(false);
    setLoading(true);
    try {
      const res = await axios.post('/api/contact', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim()
      });
      if (res.data.success) {
        setSuccess(true);
        setFormData({ name: isAuthenticated && user ? (user.username || '') : '', email: isAuthenticated && user ? (user.email || '') : '', subject: '', message: '' });
      } else {
        setError(res.data.message || 'Failed to send.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send. Try hello@cognidaily.io');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead title="Contact Us" description="Get in touch with the Cognidaily team." url="https://cognidaily.io/contact" />
      <article className="contact-page">
        <header className="contact-page__header">
          <h1 className="contact-page__title">Contact Us</h1>
          <p className="contact-page__intro">
            We&apos;d love to hear from you. Send us a message and we&apos;ll get back to you soon.
          </p>
        </header>

        <section className="contact-page__form-section card">
          {success && (
            <p role="status" className="contact-page__status contact-page__status--success">
              Message sent. We&apos;ll get back to you soon.
            </p>
          )}
          {error && (
            <p role="alert" className="contact-page__status contact-page__status--error">
              {error}
            </p>
          )}
          <form onSubmit={handleSubmit} className="contact-page__form">
            <div className="form-field">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={loading || !!isAuthenticated}
              />
            </div>
            <div className="form-field">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading || !!isAuthenticated}
              />
            </div>
            <div className="form-field">
              <label htmlFor="subject">Subject *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>
            <div className="form-field">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                required
                disabled={loading}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send'}
            </button>
          </form>
        </section>

        <section className="contact-page__fallback">
          <p>Or email us directly:</p>
          <a href="mailto:hello@cognidaily.io" className="link">hello@cognidaily.io</a>
        </section>
      </article>
    </>
  );
};

export default ContactUs;
